/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable max-len */
/**
 * @file MessageManager.ts
 * @description 全局 Message 管理器（非 React Context）
 */
import type {MessageInstance} from 'antd/es/message/interface';
import type {ModalStaticFunctions} from 'antd/es/modal/confirm';
import type {NotificationInstance} from 'antd/es/notification/interface';
import type {ArgsProps} from 'antd/es/notification/interface';

class MessageManager {
    private static instance: MessageManager;
    private messageInstance: MessageInstance | null = null;
    private modalInstance: Omit<ModalStaticFunctions, 'warn'> | null = null;
    private notificationInstance: NotificationInstance | null = null;

    private constructor() {}
    /**
     * 获取 message 实例
     */
    get message(): MessageInstance {
        if (!this.messageInstance) {
            // 返回空操作对象避免报错
            return {
                success: () => {},
                error: () => {},
                warning: () => {},
                info: () => {},
                loading: () => () => {},
                open: () => {},
                destroy: () => {}
            } as any;
        }
        return this.messageInstance;
    }

    /**
     * 获取 modal 实例
     */
    get modal(): Omit<ModalStaticFunctions, 'warn'> {
        if (!this.modalInstance) {
            return {} as any;
        }
        return this.modalInstance;
    }

    /**
     * 获取原始 notification 实例（内部使用）
     */
    private get rawNotification(): NotificationInstance {
        if (!this.notificationInstance) {
            return {
                success: () => {},
                error: () => {},
                warning: () => {},
                info: () => {},
                open: () => {},
                destroy: () => {}
            } as any;
        }
        return this.notificationInstance;
    }

    /**
     * 获取 notification 实例（包装 error 方法以应用统一配置）
     */
    get notification(): NotificationInstance {
        const raw = this.rawNotification;
        return {
            ...raw,
            error: (config: ArgsProps) => {
                // 合并配置：用户配置优先，未提供的字段使用默认值
                const {
                    message = '错误提示',
                    duration = 3,
                    placement = 'topRight' as const,
                    ...rest
                } = config;

                const finalConfig: ArgsProps = {
                    message,
                    duration,
                    placement,
                    ...rest
                };
                return raw.error(finalConfig);
            }
        };
    }

    static getInstance(): MessageManager {
        if (!MessageManager.instance) {
            MessageManager.instance = new MessageManager();
        }
        return MessageManager.instance;
    }

    /**
     * 设置 message 实例（从 useApp 中获取）
     *
     * 包装一层：所有调用自动注入 className="fm-console-message"，
     * 这样主应用的暗色 message 样式（src/entries/App/index.module.less）只在主应用生效，
     * 不会污染嵌入的子应用（如 fm-admin-fe）的 message 渲染。
     */
    setMessage(message: MessageInstance) {
        const MESSAGE_CLASS = 'fm-console-message';

        const withClassName = (
            args: string | React.ReactNode | Record<string, unknown>,
            duration?: number | (() => void),
            onClose?: () => void
        ): Record<string, unknown> => {
            if (args && typeof args === 'object' && !Array.isArray(args) && !('$$typeof' in (args as object))) {
                const config = args as Record<string, unknown>;
                const existing = (config.className as string) || '';
                return {
                    ...config,
                    className: existing ? `${existing} ${MESSAGE_CLASS}` : MESSAGE_CLASS
                };
            }
            return {
                content: args,
                duration: typeof duration === 'number' ? duration : undefined,
                onClose: typeof duration === 'function' ? duration : onClose,
                className: MESSAGE_CLASS
            };
        };

        const wrapType = (type: 'success' | 'error' | 'warning' | 'info' | 'loading') =>
            ((args: any, duration?: any, onClose?: any) =>
                (message as any).open({type, ...withClassName(args, duration, onClose)})) as MessageInstance[typeof type];

        this.messageInstance = {
            ...message,
            success: wrapType('success'),
            error: wrapType('error'),
            warning: wrapType('warning'),
            info: wrapType('info'),
            loading: wrapType('loading'),
            open: (config: any) => (message as any).open(withClassName(config))
        } as MessageInstance;
    }

    /**
     * 设置 modal 实例（从 useApp 中获取）
     */
    setModal(modal: Omit<ModalStaticFunctions, 'warn'>) {
        this.modalInstance = modal;
    }

    /**
     * 设置 notification 实例（从 useApp 中获取）
     *
     * 包装一层：所有调用自动注入 className="fm-console-notification"，
     * 避免主应用的暗色 notification 样式污染嵌入的子应用。
     */
    setNotification(notification: NotificationInstance) {
        const NOTIFICATION_CLASS = 'fm-console-notification';

        const withClassName = (config: ArgsProps): ArgsProps => {
            const existing = config.className || '';
            return {
                ...config,
                className: existing ? `${existing} ${NOTIFICATION_CLASS}` : NOTIFICATION_CLASS
            };
        };

        this.notificationInstance = {
            ...notification,
            success: (config: ArgsProps) => notification.success(withClassName(config)),
            error: (config: ArgsProps) => notification.error(withClassName(config)),
            warning: (config: ArgsProps) => notification.warning(withClassName(config)),
            info: (config: ArgsProps) => notification.info(withClassName(config)),
            open: (config: ArgsProps) => notification.open(withClassName(config))
        } as NotificationInstance;
    }

    /**
     * 便捷方法：展示错误通知
     * @param message 错误标题
     * @param description 错误详情
     * @param options 其他配置项
     */
    showError(message: string, description?: string, options?: Partial<ArgsProps>) {
        this.rawNotification.error({
            message: '错误提示',
            description: message,
            ...options
        });
    }

    /**
     * 便捷方法：展示成功通知
     * @param message 成功标题
     * @param description 成功详情
     * @param options 其他配置项
     */
    showSuccess(message: string, description?: string, options?: Partial<ArgsProps>) {
        this.rawNotification.success({
            message,
            description,
            duration: 4.5,
            placement: 'topRight',
            ...options
        });
    }

    /**
     * 便捷方法：展示警告通知
     * @param message 警告标题
     * @param description 警告详情
     * @param options 其他配置项
     */
    showWarning(message: string, description?: string, options?: Partial<ArgsProps>) {
        this.rawNotification.warning({
            message,
            description,
            duration: 4.5,
            placement: 'topRight',
            ...options
        });
    }

    /**
     * 便捷方法：展示信息通知
     * @param message 信息标题
     * @param description 信息详情
     * @param options 其他配置项
     */
    showInfo(message: string, description?: string, options?: Partial<ArgsProps>) {
        this.rawNotification.info({
            message,
            description,
            duration: 4.5,
            placement: 'topRight',
            ...options
        });
    }

    /**
     * 统一销毁所有消息和通知
     * 用于路由跳转等场景，清除所有未关闭的提示
     */
    destroyAll() {
        this.message.destroy();
        this.rawNotification.destroy();
    }
}

// 导出单例
export const messageManager = MessageManager.getInstance();

/**
 * 便捷导出：直接调用的通知方法
 * 使用方式：showError('错误信息', '详细描述')
 */
export const showError = (message: string, description?: string, options?: Partial<ArgsProps>) =>
    messageManager.showError(message, description, options);

export const showSuccess = (message: string, description?: string, options?: Partial<ArgsProps>) =>
    messageManager.showSuccess(message, description, options);

export const showWarning = (message: string, description?: string, options?: Partial<ArgsProps>) =>
    messageManager.showWarning(message, description, options);

export const showInfo = (message: string, description?: string, options?: Partial<ArgsProps>) =>
    messageManager.showInfo(message, description, options);

/**
 * 便捷导出：notification 实例
 * 用于 baseHandleError 等需要完整 notification API 的场景
 */
export const notification = messageManager.notification;
